<script setup lang="ts">
import { computed, ref } from 'vue';
import { RouterLink, RouterView, useRoute, useRouter } from 'vue-router';
import {
  BookOpen,
  Building2,
  Clock3,
  Home,
  Menu,
  Search,
  Settings,
  Star,
  Table2,
  UserRound,
  X,
} from '@lucide/vue';
import NewFileMenu from './NewFileMenu.vue';
import { api } from '../lib/api';
import { useAppStore } from '../stores/app';
import defaultLogoUrl from '../assets/intertalk-logo.png';
import type { DocumentFileType } from '../types';

const store = useAppStore();
const route = useRoute();
const router = useRouter();
const sidebarOpen = ref(false);
const searchOpen = ref(false);
const searchQuery = ref('');
const searching = ref(false);
const searchResults = ref<
  Array<{
    id: string;
    title: string;
    fileType: DocumentFileType;
    knowledgeBaseId: string;
    knowledgeBaseName: string;
    updatedAt: string;
  }>
>([]);

const knowledgeBases = computed(() => store.bootstrap?.knowledgeBases ?? []);
const editableBases = computed(() =>
  knowledgeBases.value.filter((base) => ['admin', 'editor'].includes(base.accessRole)),
);
const personalBases = computed(() => knowledgeBases.value.filter((base) => base.kind === 'personal'));
const teamBases = computed(() => knowledgeBases.value.filter((base) => base.kind === 'team'));
const initials = computed(() => store.user?.name.slice(0, 1).toUpperCase() ?? 'U');
const roleLabel = computed(() => (store.user?.role === 'admin' ? '管理员' : '普通用户'));
const editorMode = computed(() => route.name === 'document');
const logoUrl = computed(() => store.publicSettings.logoUrl ?? defaultLogoUrl);
const projectName = computed(() => store.publicSettings.projectName);

async function createFile(fileType: DocumentFileType) {
  const first = editableBases.value[0];
  if (!first) {
    await router.push({ name: 'dashboard', query: { create: 'knowledge-base' } });
    return;
  }
  const result = await api.createFile(first.id, fileType);
  await store.loadBootstrap(true);
  await router.push({
    name: 'document',
    params: { knowledgeBaseId: first.id, documentId: result.id },
  });
  sidebarOpen.value = false;
}

async function runSearch() {
  if (!searchQuery.value.trim()) return;
  searching.value = true;
  searchOpen.value = true;
  try {
    searchResults.value = (await api.search(searchQuery.value)).results;
  } finally {
    searching.value = false;
  }
}

async function openResult(result: (typeof searchResults.value)[number]) {
  searchOpen.value = false;
  searchQuery.value = '';
  await router.push({
    name: 'document',
    params: { knowledgeBaseId: result.knowledgeBaseId, documentId: result.id },
  });
}

</script>

<template>
  <div class="app-shell" :class="{ 'is-editor-mode': editorMode }">
    <button
      v-if="!editorMode"
      class="mobile-menu-button icon-button"
      type="button"
      aria-label="打开主导航"
      @click="sidebarOpen = true"
    >
      <Menu :size="21" aria-hidden="true" />
    </button>

    <div
      v-if="!editorMode && sidebarOpen"
      class="sidebar-scrim"
      role="presentation"
      @click="sidebarOpen = false"
    />

    <aside v-if="!editorMode" class="global-sidebar" :class="{ 'is-open': sidebarOpen }" aria-label="主导航">
      <div class="brand-row">
        <RouterLink class="brand sidebar-workspace" to="/app" @click="sidebarOpen = false">
          <span class="brand-mark">
            <img class="brand-logo" :src="logoUrl" alt="" />
          </span>
          <span>
            <strong>{{ projectName }}</strong>
            <small>知识工作台</small>
          </span>
        </RouterLink>
        <div class="sidebar-brand-actions">
          <RouterLink
            :to="{ name: 'settings' }"
            class="sidebar-avatar-link"
            :aria-label="`打开设置，当前身份：${roleLabel}`"
            :title="roleLabel"
            @click="sidebarOpen = false"
          >
            <span class="user-avatar" :style="{ backgroundColor: store.user?.avatarColor }">
              {{ initials }}
            </span>
          </RouterLink>
          <button
            class="icon-button sidebar-close"
            type="button"
            aria-label="关闭主导航"
            @click="sidebarOpen = false"
          >
            <X :size="20" aria-hidden="true" />
          </button>
        </div>
      </div>

      <NewFileMenu class="sidebar-create" :disabled="!store.canEdit" @select="createFile" />

      <div class="sidebar-search-area">
        <form class="sidebar-search" role="search" @submit.prevent="runSearch">
          <Search :size="17" aria-hidden="true" />
          <label class="sr-only" for="global-search">搜索知识库</label>
          <input
            id="global-search"
            v-model="searchQuery"
            type="search"
            placeholder="搜索文件"
            autocomplete="off"
            @focus="searchOpen = searchResults.length > 0"
          />
          <kbd>Ctrl K</kbd>
        </form>

        <div v-if="searchOpen" class="search-popover">
          <div class="search-popover-head">
            <span>{{ searching ? '正在搜索…' : `找到 ${searchResults.length} 条结果` }}</span>
            <button type="button" class="text-button" @click="searchOpen = false">关闭</button>
          </div>
          <button
            v-for="result in searchResults"
            :key="result.id"
            class="search-result"
            type="button"
            @click="openResult(result)"
          >
            <Table2 v-if="result.fileType === 'spreadsheet'" :size="16" aria-hidden="true" />
            <BookOpen v-else :size="16" aria-hidden="true" />
            <span><strong>{{ result.title }}</strong><small>{{ result.knowledgeBaseName }}</small></span>
          </button>
          <p v-if="!searching && searchResults.length === 0" class="search-empty">
            没有找到相关文件
          </p>
        </div>
      </div>

      <nav class="sidebar-nav sidebar-primary-nav">
        <RouterLink to="/app" exact-active-class="is-active" @click="sidebarOpen = false">
          <Home :size="18" aria-hidden="true" />工作台
        </RouterLink>
        <a href="/app#recent" @click="sidebarOpen = false">
          <Clock3 :size="18" aria-hidden="true" />最近文件
        </a>
        <a href="/app#knowledge-bases" @click="sidebarOpen = false">
          <Star :size="18" aria-hidden="true" />我的知识库
        </a>
      </nav>

      <div class="sidebar-section">
        <div class="sidebar-section-title">
          <span>知识库</span>
          <span>{{ knowledgeBases.length }}</span>
        </div>

        <section class="knowledge-nav-group" aria-labelledby="personal-base-title">
          <div id="personal-base-title" class="knowledge-nav-heading">
            <span><UserRound :size="14" aria-hidden="true" />个人知识库</span>
            <span>{{ personalBases.length }}</span>
          </div>
          <nav class="knowledge-nav" aria-label="个人知识库">
            <RouterLink
              v-for="base in personalBases"
              :key="base.id"
              :to="{ name: 'knowledge-base', params: { knowledgeBaseId: base.id } }"
              @click="sidebarOpen = false"
            >
              <span class="knowledge-dot" :style="{ backgroundColor: base.color }" />
              <span>{{ base.name }}</span>
            </RouterLink>
            <p v-if="personalBases.length === 0" class="knowledge-nav-empty">还没有个人知识库</p>
          </nav>
        </section>

        <section class="knowledge-nav-group" aria-labelledby="team-base-title">
          <div id="team-base-title" class="knowledge-nav-heading">
            <span><Building2 :size="14" aria-hidden="true" />团队知识库</span>
            <span>{{ teamBases.length }}</span>
          </div>
          <nav class="knowledge-nav" aria-label="团队知识库">
            <RouterLink
              v-for="base in teamBases"
              :key="base.id"
              :to="{ name: 'knowledge-base', params: { knowledgeBaseId: base.id } }"
              @click="sidebarOpen = false"
            >
              <span class="knowledge-dot" :style="{ backgroundColor: base.color }" />
              <span>{{ base.name }}</span>
            </RouterLink>
            <p v-if="teamBases.length === 0" class="knowledge-nav-empty">还没有团队知识库</p>
          </nav>
        </section>
      </div>

      <nav class="sidebar-nav admin-nav">
        <RouterLink :to="{ name: 'settings' }" active-class="is-active" @click="sidebarOpen = false">
          <Settings :size="18" aria-hidden="true" />设置
        </RouterLink>
      </nav>
    </aside>

    <main id="main-content" class="app-main" tabindex="-1">
      <RouterView />
    </main>
  </div>
</template>
